import {
  IonBadge,
  IonCardSubtitle,
  IonContent,
  IonFooter,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSpinner,
  IonToolbar,
  useIonActionSheet,
} from '@ionic/react'
import { msg, plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import {
  cloudOfflineOutline,
  globeOutline,
  helpCircleOutline,
  logOutOutline,
  navigateOutline,
  syncOutline,
} from 'ionicons/icons'
import { DateTime } from 'luxon'
import React from 'react'
import { DebugHandler } from '../components/DebugHandler'
import { reloadTo } from '../helpers/appReload'
import { GreetingHeader } from '../components/GreetingHeader'
import { OrgSwitcherItem } from '../components/OrgSwitcherItem'
import { VersionAndBuildInfo } from '../components/VersionAndBuildInfo'
import { useSyncStatus } from '../context/SyncStatusProvider'
import {
  NAVIGATORS,
  type NavigationApp,
  defaultNavigationApp,
  getNavApp,
  navigators,
  setNavApp,
} from '../helpers/navigate'
import { openExternalUrl } from '../helpers/openExternalUrl'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { useNetwork } from '../hooks/useNetwork'
import { useSession } from '../hooks/useSession'
import { getLocale, setLocale } from '../i18n'

const HELP_URL = 'https://www.cretesuite.com/embeds/crewapp/crew-faq'
const PRIVACY_POLICY_URL = 'https://www.cretesuite.com/legal/privacy-policy'
const TERMS_OF_USE_URL = 'https://www.cretesuite.com/legal/terms-of-use'

const LANG_NAMES: Record<string, string> = {
  en: 'English',
  es: 'Español',
}

/** Background sync failures (server rejected a queued change) on the Sync row. */
const SyncFailuresSubtitle = ({ failureCount }: { failureCount: number }) => {
  const { _ } = useLingui()
  if (failureCount === 0) return null
  return (
    <IonCardSubtitle
      style={{
        marginTop: 2,
        fontSize: '0.8em',
        color: 'var(--ion-color-danger)',
      }}
    >
      {_(
        plural(failureCount, {
          one: '# change could not be synced',
          other: '# changes could not be synced',
        }),
      )}
    </IonCardSubtitle>
  )
}

export const ProfilePage = () => {
  const { logout } = useSession()
  const { user } = useCurrentUser()
  const { _ } = useLingui()
  const { isOnline } = useNetwork()
  const { lastSyncAt, pendingCount, failureCount, isSyncing, forceSync } =
    useSyncStatus()
  const [presentActionSheet] = useIonActionSheet()
  const [navApp, setNavAppState] = React.useState<NavigationApp>(
    () => getNavApp() ?? defaultNavigationApp(),
  )

  const currentLocale = getLocale()
  const currentLangName = LANG_NAMES[currentLocale] || 'English'

  const lastSyncRelative = lastSyncAt
    ? (DateTime.fromISO(lastSyncAt).toRelative({ locale: currentLocale }) ?? '')
    : ''

  const handleSyncNow = () => {
    if (!isOnline || isSyncing) return
    forceSync()
  }

  const handleNavAppSelect = () => {
    presentActionSheet({
      header: _(msg`Select Default Navigation App`),
      buttons: [
        ...navigators().map(([key, reg]) => ({
          text: reg.title,
          cssClass: key === navApp ? 'action-sheet-default' : undefined,
          handler: () => {
            setNavApp(key)
            setNavAppState(key)
          },
        })),
        {
          text: _(msg`Cancel`),
          role: 'cancel',
        },
      ],
    })
  }

  const handleLanguageSelect = () => {
    presentActionSheet({
      header: _(msg`Select Language`),
      buttons: [
        {
          text: 'English',
          handler: () => {
            setLocale('en')
          },
        },
        {
          text: 'Español',
          handler: () => {
            setLocale('es')
          },
        },
        {
          text: _(msg`Cancel`),
          role: 'cancel',
        },
      ],
    })
  }

  return (
    <IonPage>
      <GreetingHeader name={user?.firstName ?? undefined} />
      <IonContent className='profile-gray'>
        <IonList lines='full'>
          <IonItem button detail onClick={handleNavAppSelect}>
            <IonIcon icon={navigateOutline} slot='start' />
            <IonLabel>
              {_(msg`Default Navigation App`)}
              <IonCardSubtitle style={{ marginTop: 4, fontSize: '0.8em' }}>
                {NAVIGATORS[navApp].title}
              </IonCardSubtitle>
            </IonLabel>
          </IonItem>
          <IonItem button detail onClick={() => openExternalUrl(HELP_URL)}>
            <IonIcon icon={helpCircleOutline} slot='start' />
            <IonLabel>{_(msg`Help`)}</IonLabel>
          </IonItem>
          <IonItem button detail onClick={handleLanguageSelect}>
            <IonIcon icon={globeOutline} slot='start' />
            <IonLabel>
              {_(msg`Language`)}
              <IonCardSubtitle style={{ marginTop: 4, fontSize: '0.8em' }}>
                {currentLangName}
              </IonCardSubtitle>
            </IonLabel>
          </IonItem>
          <OrgSwitcherItem />
          <IonItem
            button={isOnline && !isSyncing}
            detail={false}
            onClick={handleSyncNow}
          >
            <IonIcon
              icon={isOnline ? syncOutline : cloudOfflineOutline}
              slot='start'
            />
            <IonLabel>
              {isOnline ? _(msg`Sync now`) : _(msg`Offline`)}
              <IonCardSubtitle style={{ marginTop: 4, fontSize: '0.8em' }}>
                {pendingCount > 0
                  ? _(
                      plural(pendingCount, {
                        one: '# change waiting to sync',
                        other: '# changes waiting to sync',
                      }),
                    )
                  : isOnline
                    ? _(msg`Up to date`)
                    : lastSyncAt
                      ? _(msg`Last synced ${lastSyncRelative}`)
                      : _(msg`Not synced yet`)}
              </IonCardSubtitle>
              <SyncFailuresSubtitle failureCount={failureCount} />
            </IonLabel>
            {isSyncing ? (
              <IonSpinner slot='end' name='crescent' />
            ) : pendingCount > 0 ? (
              <IonBadge slot='end' color='warning'>
                {pendingCount}
              </IonBadge>
            ) : null}
          </IonItem>
          <IonItem
            button
            detail={false}
            onClick={async () => {
              // Await so the graphcache wipe finishes before the reload
              await logout()
              // Force full reload — Ionic caches view stacks and the
              // IsAuthenticatedProvider doesn't always re-render immediately
              reloadTo('/')
            }}
          >
            <IonIcon icon={logOutOutline} slot='start' />
            <IonLabel>{_(msg`Logout`)}</IonLabel>
          </IonItem>
        </IonList>
      </IonContent>
      <IonFooter className='ion-no-border'>
        <IonToolbar>
          <div style={{ textAlign: 'center', padding: '8px 16px' }}>
            <DebugHandler>
              <img
                src='/assets/cs-logo-poweredby.svg'
                alt='Powered by CreteSuite'
                style={{ height: 40, margin: '0 auto 12px', display: 'block' }}
              />
            </DebugHandler>
            <div>
              <button
                type='button'
                onClick={() => openExternalUrl(PRIVACY_POLICY_URL)}
                style={{
                  textDecoration: 'underline',
                  fontWeight: 600,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'inherit',
                  fontSize: '0.85em',
                }}
              >
                {_(msg`Privacy Policy`)}
              </button>
              <span
                style={{
                  padding: '0 7px',
                  fontSize: '0.65em',
                  verticalAlign: 'top',
                  lineHeight: '20px',
                }}
              >
                |
              </span>
              <button
                type='button'
                onClick={() => openExternalUrl(TERMS_OF_USE_URL)}
                style={{
                  textDecoration: 'underline',
                  fontWeight: 600,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'inherit',
                  fontSize: '0.85em',
                }}
              >
                {_(msg`Terms of Use`)}
              </button>
            </div>
            <hr
              style={{
                width: '100%',
                opacity: 1,
                borderTop: '1px solid var(--cs-color-border, #dedede)',
                height: 0,
                marginTop: '12px',
              }}
            />
            <VersionAndBuildInfo />
          </div>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  )
}

export default ProfilePage
